import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminQA() {
  const [stats, setStats] = useState(null);
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [volume, setVolume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [showContent, setShowContent] = useState(false);
  const [revealedQuestions, setRevealedQuestions] = useState({});

  useEffect(() => {
    fetchData();
  }, [days, showContent]);

  const fetchData = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const [statsRes, questionsRes, volumeRes] = await Promise.all([
        axios.get(`${API}/admin/qa/stats?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/qa/recent-questions?limit=20&show_content=${showContent}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/qa/volume?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setStats(statsRes.data);
      setRecentQuestions(questionsRes.data.questions);
      setVolume(volumeRes.data);
    } catch (error) {
      toast.error("Failed to load Q&A data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevealContent = async (questionId) => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.post(
        `${API}/admin/qa/reveal-content/${questionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRevealedQuestions(prev => ({
        ...prev,
        [questionId]: {
          question: response.data.question,
          answer: response.data.answer
        }
      }));
      
      toast.success("Content revealed (action logged in audit)");
    } catch (error) {
      toast.error("Failed to reveal content");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading Q&A data...</p>
        </div>
      </div>
    );
  }

  const maxQuestions = Math.max(...(volume?.timeline || []).map(t => t.questions), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Q&A Monitor</h1>
          <p className="text-muted-foreground mt-1">Question analytics and AI performance</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="60">Last 60 days</option>
        </select>
      </div>

      {/* Stats KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm mb-2">Total Questions</p>
          <p className="text-3xl font-bold text-foreground">{stats?.total_questions || 0}</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm mb-2">Recent ({days}d)</p>
          <p className="text-3xl font-bold text-foreground">{stats?.recent_questions || 0}</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm mb-2">Saved Answers</p>
          <p className="text-3xl font-bold text-purple-400">{stats?.saved_answers || 0}</p>
          <p className="text-muted-foreground text-xs mt-1">{stats?.save_rate || 0}% save rate</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm mb-2">Avg Response Time</p>
          <p className="text-3xl font-bold text-foreground">{stats?.avg_response_time_seconds || 0}s</p>
        </div>
      </div>

      {/* Question Volume Chart */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Question Volume Trend</h2>
        <div className="flex items-end justify-between gap-1 h-48">
          {volume?.timeline?.map((day, idx) => {
            const height = (day.questions / maxQuestions) * 100;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center justify-end group">
                <div className="relative w-full">
                  <div
                    className="w-full bg-gradient-to-t from-purple-500 to-purple-400 rounded-t transition-all hover:from-purple-400 hover:to-purple-300"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  />
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 hidden group-hover:block bg-muted px-2 py-1 rounded text-xs text-foreground whitespace-nowrap">
                    <p>{day.questions} questions</p>
                    <p className="text-green-400">{day.saved} saved</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Question Type Breakdown & Recent Questions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Type Breakdown */}
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Question Type Distribution</h2>
          <div className="space-y-3">
            {stats?.question_type_breakdown && Object.entries(stats.question_type_breakdown).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-foreground capitalize">{type || "Standard"}</span>
                <span className="text-muted-foreground font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Questions */}
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">Recent Questions</h2>
            <div className="px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-400 text-xs">
              🔒 Privacy Protected
            </div>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {recentQuestions.slice(0, 5).map((q) => (
              <div key={q.id} className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-muted-foreground text-xs">User {q.user_id_short}</p>
                  <span className="text-muted-foreground text-xs">
                    {new Date(q.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-foreground text-sm">
                  {revealedQuestions[q.id] ? revealedQuestions[q.id].question : q.question_preview}
                </p>
                {!revealedQuestions[q.id] && (
                  <button
                    onClick={() => handleRevealContent(q.id)}
                    className="mt-2 px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-foreground text-xs rounded transition"
                  >
                    Reveal Content (Logs to Audit)
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
